import { t } from 'i18next';

import { AdminHealthOverview, AdminWorkerMachine } from '@activepieces/shared';

import { RESOURCE_ALERT_PERCENT } from './constants';

// The worst machine is what matters at a glance: six workers at 10% and five
// at 10% with one at 98% both average low, but only one of them is in trouble.
export const busiestCpuPercentage = (
  machines: AdminHealthOverview['machines'],
): number | null =>
  machines.length === 0
    ? null
    : Math.max(...machines.map((machine) => machine.cpuUsagePercentage));

const Usage = ({ label, percent }: { label: string; percent: number }) => (
  <span className="flex items-baseline gap-1.5">
    <span className="text-muted-foreground">{label}</span>
    <span
      className={
        percent >= RESOURCE_ALERT_PERCENT
          ? 'font-medium tabular-nums text-destructive'
          : 'font-medium tabular-nums'
      }
    >
      {t('{percent}%', { percent: Math.round(percent) })}
    </span>
  </span>
);

// Stacked rather than on one row: the tile keeps its third of the grid when it
// opens, so the detail has to read at that width.
const MachineLine = ({ machine }: { machine: AdminWorkerMachine }) => (
  <div className="flex flex-col gap-0.5">
    <span className="font-mono text-xs text-muted-foreground truncate">
      {machine.ip || machine.id}
    </span>
    <span className="flex items-baseline gap-3 text-sm flex-wrap">
      <Usage label={t('CPU')} percent={machine.cpuUsagePercentage} />
      <Usage label={t('RAM')} percent={machine.ramUsagePercentage} />
      <Usage label={t('Disk')} percent={machine.diskUsagePercentage} />
    </span>
  </div>
);

// The list only: it lives inside the "Workers online" tile, which expands
// rather than spawning a second card beside it.
export const MachineLines = ({
  machines,
}: {
  machines: AdminHealthOverview['machines'];
}) => (
  <div className="flex flex-col gap-2 px-4 py-3.5 border-t">
    {machines.length === 0 ? (
      <p className="text-sm text-destructive">
        {t('No worker is online — nothing is executing flows.')}
      </p>
    ) : (
      machines.map((machine) => (
        <MachineLine key={machine.id} machine={machine} />
      ))
    )}
  </div>
);
